import { defineStore } from 'pinia'
import type { ArticleRecord, CategoryRecord } from '~/types/datocms'

export interface BlogStoreState {
  allArticles: ArticleRecord[]
  allArticlesCount: number
  allCategories: CategoryRecord[]
  isLoading: boolean
  isLoadingMore: boolean
  itemsPerPage: number
  isButtonVisible: boolean
  skipItems: number
  filteredBy: CategoryRecord | null
}

export const useBlogStore = defineStore('BlogStore', {
  state: (): BlogStoreState => ({
    allArticles: [],
    allArticlesCount: 0,
    allCategories: [],
    isLoading: true,
    isLoadingMore: false,
    itemsPerPage: 10,
    isButtonVisible: false,
    skipItems: 0,
    filteredBy: null
  }),
  getters: {
    getCategoryBySlug(state) {
      return (catSlug: string) => state.allCategories.find((cat) => cat.slug === catSlug) || null
    }
  },
  actions: {
    async fetchArticles(filter?: string) {
      this.setButtonVisibility(false)
      try {
        const { data } = await useArticles({
          variables: {
            first: this.itemsPerPage,
            skip: this.skipItems,
            allInCategory: filter as unknown as string[]
          }
        })

        this.allArticlesCount = data._allArticlesMeta.count

        const allArticlesRes: ArticleRecord[] = data.allArticles as ArticleRecord[]

        if (allArticlesRes.length > 0) {
          this.allArticles.push(...allArticlesRes)
          this.skipItems += allArticlesRes.length
        }
      } catch (error) {
        console.error(error)
      } finally {
        useTimeoutFn(() => {
          this.setLoading(false)
          this.setLoadingMore(false)
        }, 600)

        useTimeoutFn(() => {
          this.setButtonVisibility()
        }, 1000)
      }
    },
    pushCategories(payload: BlogStoreState['allCategories']) {
      this.allCategories = payload
    },
    pushFilter(category: BlogStoreState['filteredBy']) {
      this.filteredBy = category
    },
    setItemsPerPage(amount: number) {
      this.itemsPerPage = amount
    },
    setLoading(bool: boolean) {
      this.isLoading = bool
    },
    setLoadingMore(bool: boolean) {
      this.isLoadingMore = bool
    },
    setButtonVisibility(bool?: boolean) {
      this.isButtonVisible = bool ? bool : this.skipItems < this.allArticlesCount
    },
    resetArticles() {
      this.allArticles = []
      this.allArticlesCount = 0
      this.skipItems = 0
    }
  }
})
